// Shared AI provider configuration and request helper used by the AI report
// panels (AIReportPanel, AICoachPanel).

export const kOpenAIKeyStorage = "VBLiveOpenAIKey";
export const kAIProviderStorage = "VBLiveAIProvider";
export const kAICoachLanguageStorage = "VBLiveAICoachLanguage";

// Reusable primer that teaches the model how volleyball scoring actually works.
// Models frequently mis-handle volleyball results by summing points across sets
// like basketball, which is wrong. Prepend this to every AI system prompt so the
// analysis reasons about sets and match outcome correctly.
export const kVolleyballScoringPrimer =
  "CRITICAL VOLLEYBALL SCORING RULES — read carefully and never violate them:\n" +
  "1. A volleyball match is divided into separate SETS (best-of-5, or sometimes " +
  "best-of-3). Each set is completely self-contained and is won by the first team " +
  "to reach the set target (25 points, or 15 in a deciding 5th set) with at least a " +
  "two-point margin.\n" +
  "2. The MATCH is won by the team that wins the MAJORITY of sets (e.g. 3 sets in a " +
  "best-of-5). The match result is decided ONLY by the count of sets won.\n" +
  "3. You must NEVER, under any circumstances, add up or compare cumulative point " +
  "totals across sets to decide who is ahead, who is winning, or who won. This is " +
  "NOT basketball, football, or any cumulative-score sport. Total points across the " +
  "match are IRRELEVANT to the result.\n" +
  "4. A team can score FAR MORE total points across the whole match and STILL LOSE " +
  "the match. For example, with set scores 19-25, 25-19, 27-25, 21-25, 12-15, Team A " +
  "scored 104 points and Team B scored 109, but more importantly Team A won only 2 " +
  "sets (set 2 and set 3) and lost 3 sets, so Team A LOST the match 2-3. A 'point " +
  "advantage' or 'leading on total points' means NOTHING.\n" +
  "5. Never write phrases like 'Team A holds a X-point advantage' or 'leading " +
  "87-67' or 'ahead by multiple sets based on points'. If you do not have the set " +
  "scores, do NOT infer the match result from point totals at all — instead say the " +
  "result cannot be determined from totals alone.\n" +
  "6. Any aggregated point, kill, or efficiency totals provided to you are for " +
  "STATISTICAL analysis of play quality only. They are NOT a scoreboard and must " +
  "never be treated as a running match score. Always reason about who won in terms " +
  "of SETS WON, using the set-by-set scores when they are provided.\n" +
  "7. NEVER mention, state, compute, compare, or even reference cumulative/total " +
  "points in your report (e.g. do NOT write things like 'scored 20 more total " +
  "points', '87-67', 'despite scoring more points', or any total-points comparison). " +
  "Total points are irrelevant and must be completely omitted. Report results and " +
  "analysis using sets won and per-skill statistics (sideout %, attack efficiency, " +
  "serving, blocking, reception, errors) only.";

// Each provider exposes an OpenAI-compatible /chat/completions endpoint except
// Anthropic, which uses its own /messages shape (handled in callAI).
export const kProviders = {
  openai: {
    label: "OpenAI (GPT)",
    type: "openai",
    endpoint: "https://api.openai.com/v1/chat/completions",
    keyLabel: "OpenAI API key",
    keyPlaceholder: "sk-...",
    models: ["gpt-4o-mini", "gpt-4o", "gpt-4.1-mini", "gpt-4.1"],
  },
  anthropic: {
    label: "Anthropic (Claude)",
    type: "anthropic",
    endpoint: "https://api.anthropic.com/v1/messages",
    keyLabel: "Anthropic API key",
    keyPlaceholder: "sk-ant-...",
    models: ["claude-sonnet-4-5", "claude-opus-4-1", "claude-3-5-haiku-latest"],
  },
  gemini: {
    label: "Google (Gemini)",
    type: "openai",
    endpoint:
      "https://generativelanguage.googleapis.com/v1beta/openai/chat/completions",
    keyLabel: "Google AI API key",
    keyPlaceholder: "AIza...",
    models: ["gemini-2.5-flash", "gemini-2.5-pro", "gemini-2.0-flash"],
  },
  deepseek: {
    label: "DeepSeek",
    type: "openai",
    endpoint: "https://api.deepseek.com/chat/completions",
    keyLabel: "DeepSeek API key",
    keyPlaceholder: "sk-...",
    models: ["deepseek-chat", "deepseek-reasoner"],
  },
  mistral: {
    label: "Mistral",
    type: "openai",
    endpoint: "https://api.mistral.ai/v1/chat/completions",
    keyLabel: "Mistral API key",
    keyPlaceholder: "...",
    models: ["mistral-large-latest", "mistral-small-latest"],
  },
};

export const providerConfig = (provider) =>
  kProviders[provider] || kProviders.openai;

// OpenAI keeps the legacy key for backward compatibility.
export const keyStorageFor = (provider) =>
  provider === "openai" ? kOpenAIKeyStorage : `VBLiveAIKey_${provider}`;

export const modelStorageFor = (provider) => `VBLiveAIModel_${provider}`;

// Same-origin serverless proxy that forwards AI requests to the provider from
// the server, avoiding all browser CORS restrictions. Configurable/disable-able
// via REACT_APP_AI_PROXY_URL ("" disables the proxy and forces direct calls).
const kProxyEndpoint =
  process.env.REACT_APP_AI_PROXY_URL === undefined
    ? "/api/ai"
    : process.env.REACT_APP_AI_PROXY_URL;

// Builds the direct (browser -> provider) fetch options for a provider.
function directRequest(cfg, { apiKey, model, messages }) {
  if (cfg.type === "anthropic") {
    // Anthropic expects system text separately and only user/assistant turns.
    const systemText = messages
      .filter((m) => m.role === "system")
      .map((m) => m.content)
      .join("\n\n");
    const turns = messages
      .filter((m) => m.role !== "system")
      .map((m) => ({ role: m.role, content: m.content }));
    return {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": apiKey,
        "anthropic-version": "2023-06-01",
        "anthropic-dangerous-direct-browser-access": "true",
      },
      body: JSON.stringify({
        model: model,
        max_tokens: 4096,
        temperature: 0.4,
        system: systemText || undefined,
        messages: turns,
      }),
    };
  }
  // OpenAI-compatible providers (OpenAI, Gemini, DeepSeek, Mistral).
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${apiKey}`,
    },
    body: JSON.stringify({ model: model, messages: messages, temperature: 0.4 }),
  };
}

// Extracts the text content from a provider's raw JSON response.
function extractContent(cfg, data) {
  if (cfg.type === "anthropic") {
    return (data.content && data.content[0] && data.content[0].text) || "";
  }
  return (
    (data.choices &&
      data.choices[0] &&
      data.choices[0].message &&
      data.choices[0].message.content) ||
    ""
  );
}

// Attempts the request through the same-origin proxy. Returns the Response to
// read like a direct response when the proxy handled it (success OR a JSON
// error from the provider), or null when there is no proxy deployed and we
// should transparently fall back to a direct browser call. Throws a clear
// message when the proxy IS deployed but the platform/upstream errored (e.g. a
// serverless timeout), so we never mask that as a misleading CORS error.
async function callProxy(provider, { apiKey, model, messages }) {
  if (!kProxyEndpoint) return null;
  let resp;
  try {
    resp = await fetch(kProxyEndpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ provider, apiKey, model, messages }),
    });
  } catch (e) {
    // Could not even reach our own origin — fall back to a direct call.
    return null;
  }
  // 404 = function not deployed (e.g. plain static hosting). Fall back.
  if (resp.status === 404) return null;
  const contentType = resp.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    // A real proxy response (a provider success or a JSON error body).
    return resp;
  }
  // A 2xx non-JSON body means the dev server / SPA returned index.html for the
  // unknown /api/ai route — there is no real proxy here, so fall back.
  if (resp.ok) return null;
  // Non-2xx, non-JSON (e.g. a 504 timeout or 502 page from the host). The proxy
  // IS deployed but the platform/upstream errored. Surface a clear message
  // instead of silently falling back to a CORS-blocked direct call.
  throw new Error(
    `The AI proxy responded with an error (HTTP ${resp.status}). ` +
      "This usually means the server timed out while the AI was generating a " +
      "large response. Try a smaller request — for the deep analysis, run one " +
      "section at a time rather than all sections — and try again."
  );
}

// Sends a chat request to the selected provider and returns the text content.
// Routes through the same-origin proxy first (no CORS), falling back to a direct
// browser call when the proxy is unavailable.
export async function callAI({ provider, apiKey, model, messages }) {
  const cfg = providerConfig(provider);

  let resp = await callProxy(provider, { apiKey, model, messages });
  if (!resp) {
    resp = await fetchAI(cfg, directRequest(cfg, { apiKey, model, messages }));
  }
  if (!resp.ok) {
    throw new Error(await errorMessage(resp));
  }
  const data = await resp.json();
  return extractContent(cfg, data);
}

async function errorMessage(resp) {
  let message = `Request failed (${resp.status})`;
  try {
    const errBody = await resp.json();
    if (errBody && errBody.error && errBody.error.message) {
      message = errBody.error.message;
    }
  } catch (e) {
    // ignore parse error
  }
  return message;
}

// Wraps fetch so that a network-level failure (the browser's "Failed to fetch"
// TypeError, thrown before any HTTP response) becomes a clear, actionable
// message. This is almost always caused by the browser blocking the
// cross-origin request (CORS), no internet connection, or an ad-blocker /
// privacy extension intercepting the call.
async function fetchAI(cfg, options) {
  try {
    return await fetch(cfg.endpoint, options);
  } catch (e) {
    throw new Error(
      `Could not reach ${cfg.label} at ${cfg.endpoint}. ` +
        "The request was blocked before it completed — this is usually a CORS " +
        "restriction (the provider does not allow calls directly from a browser), " +
        "no internet connection, or an ad-blocker/privacy extension. Check your " +
        "connection, disable blocking extensions for this site, or use a provider " +
        "that permits browser access."
    );
  }
}
